import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import { ArrowRight, Loader2, ArrowUpRight, Calendar, Tag } from "lucide-react";
import EmailCapture from "../components/EmailCapture";
import { trackAffiliateClick, trackCTAClick } from "../lib/analytics";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

const InlineCTA = ({ cta, testId }) => {
  if (!cta?.text || !cta?.url) return null;
  const isExternal = /^https?:\/\//i.test(cta.url) || cta.url.startsWith("/api/");
  const href = cta.url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${cta.url}` : cta.url;
  const onClick = () => {
    if (isExternal) {
      const category = /booking|hotel|expedia/i.test(cta.url) ? "hotel" : /getyourguide|gyg|activit/i.test(cta.url) ? "experience" : "affiliate";
      trackAffiliateClick({ label: cta.text, category, destination: href, location: testId });
    } else {
      trackCTAClick({ label: cta.text, location: testId, destination: cta.url });
    }
  };
  const linkContent = (
    <>
      {cta.text} {isExternal ? <ArrowUpRight className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
    </>
  );
  return (
    <div className="my-10 not-prose" data-testid={testId}>
      <div className="rounded-2xl bg-gradient-to-br from-[#FFF7E8] via-white to-[#E6F6FD] border border-[#FFB800]/20 p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
        <div>
          <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#FF8C00] mb-1">Recommended</div>
          <div className="font-serif-display text-xl md:text-2xl font-bold text-[#0B2C5B]">{cta.text}</div>
        </div>
        {isExternal ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={onClick}
            className="inline-flex items-center gap-2 btn-primary-gradient text-white rounded-full px-6 py-3 font-semibold shadow-md hover:-translate-y-0.5 transition-all whitespace-nowrap self-start md:self-auto"
          >
            {linkContent}
          </a>
        ) : (
          <Link
            to={cta.url}
            onClick={onClick}
            className="inline-flex items-center gap-2 btn-primary-gradient text-white rounded-full px-6 py-3 font-semibold shadow-md hover:-translate-y-0.5 transition-all whitespace-nowrap self-start md:self-auto"
          >
            {linkContent}
          </Link>
        )}
      </div>
    </div>
  );
};

const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    Promise.all([
      api.get(`/articles/${slug}`),
      api.get(`/articles/${slug}/related`).catch(() => ({ data: [] })),
    ])
      .then(([a, r]) => {
        setArticle(a.data);
        setRelated(r.data || []);
        document.title = a.data.meta_title || `${a.data.title} | Visit Bedford`;
        const desc = a.data.meta_description || a.data.excerpt;
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
          meta = document.createElement("meta");
          meta.name = "description";
          document.head.appendChild(meta);
        }
        meta.setAttribute("content", desc);
      })
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <Loader2 className="w-6 h-6 animate-spin text-[#0B2C5B]" />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 pt-24">
        <h1 className="font-serif-display text-4xl font-bold text-[#0B2C5B] mb-3">Article not found</h1>
        <p className="text-slate-600 mb-6">The article you're looking for doesn't exist or has been moved.</p>
        <button onClick={() => navigate("/blog")} className="btn-primary-gradient text-white rounded-full px-6 py-3 font-semibold">
          Back to Blog
        </button>
      </div>
    );
  }

  // Split content roughly in half on paragraph boundaries for mid-CTA insertion
  const paragraphs = (article.content || "").split(/\n\s*\n/);
  const mid = Math.max(1, Math.floor(paragraphs.length / 2));
  const firstHalf = paragraphs.slice(0, mid).join("\n\n");
  const secondHalf = paragraphs.slice(mid).join("\n\n");

  return (
    <article data-testid="article-page">
      {/* HERO */}
      <section className="relative h-[70vh] min-h-[520px] w-full overflow-hidden">
        <img src={article.hero_image} alt={article.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 flex items-end">
          <div className="container-site pb-16 md:pb-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              className="max-w-4xl"
            >
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-white/80 hover:text-white text-xs font-semibold tracking-[0.25em] uppercase mb-5"
                data-testid="article-back-blog"
              >
                ← The Bedford Journal
              </Link>
              <h1 className="font-serif-display text-4xl md:text-6xl lg:text-7xl font-black text-white leading-[0.95] text-balance mb-5">
                {article.title}
              </h1>
              {article.subtitle && (
                <p className="text-white/90 text-lg md:text-2xl font-light italic max-w-3xl">{article.subtitle}</p>
              )}
              <div className="flex flex-wrap items-center gap-5 mt-6 text-xs font-semibold tracking-[0.2em] uppercase text-white/80">
                <span className="inline-flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" /> {formatDate(article.published_at || article.created_at)}
                </span>
                {article.tags?.length > 0 && (
                  <span className="inline-flex items-center gap-2">
                    <Tag className="w-3.5 h-3.5" /> {article.tags.slice(0, 3).join(" · ")}
                  </span>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="bg-[#FAFAFA] py-16 md:py-24">
        <div className="container-site max-w-3xl">
          {article.excerpt && (
            <p className="font-serif-display text-2xl md:text-3xl text-[#0B2C5B] leading-relaxed mb-10 border-l-4 border-[#FFB800] pl-6 italic">
              {article.excerpt}
            </p>
          )}

          <InlineCTA cta={article.intro_cta} testId="article-intro-cta" />

          <div className="prose prose-lg max-w-none prose-headings:font-serif-display prose-headings:text-[#0B2C5B] prose-h2:text-3xl md:prose-h2:text-4xl prose-h2:font-bold prose-h2:mt-12 prose-h2:mb-5 prose-h3:text-xl md:prose-h3:text-2xl prose-h3:font-semibold prose-p:text-slate-700 prose-p:leading-relaxed prose-a:text-[#0B2C5B] prose-a:font-semibold prose-a:underline prose-a:decoration-[#FFB800] prose-a:decoration-2 prose-a:underline-offset-4 hover:prose-a:text-[#FF8C00] prose-strong:text-[#0B2C5B] prose-li:text-slate-700 prose-ul:my-5">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{firstHalf}</ReactMarkdown>
          </div>

          <InlineCTA cta={article.mid_cta} testId="article-mid-cta" />

          <div className="prose prose-lg max-w-none prose-headings:font-serif-display prose-headings:text-[#0B2C5B] prose-h2:text-3xl md:prose-h2:text-4xl prose-h2:font-bold prose-h2:mt-12 prose-h2:mb-5 prose-h3:text-xl md:prose-h3:text-2xl prose-h3:font-semibold prose-p:text-slate-700 prose-p:leading-relaxed prose-a:text-[#0B2C5B] prose-a:font-semibold prose-a:underline prose-a:decoration-[#FFB800] prose-a:decoration-2 prose-a:underline-offset-4 hover:prose-a:text-[#FF8C00] prose-strong:text-[#0B2C5B] prose-li:text-slate-700 prose-ul:my-5">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{secondHalf}</ReactMarkdown>
          </div>

          <InlineCTA cta={article.end_cta} testId="article-end-cta" />

          {/* Internal links rail */}
          <div className="mt-12 pt-10 border-t border-slate-200">
            <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#2AB7E8] mb-4">Helpful next steps</div>
            <div className="flex flex-wrap gap-3">
              <Link to="/hotels" className="inline-flex items-center gap-2 rounded-full border-2 border-[#0B2C5B] text-[#0B2C5B] px-5 py-2.5 text-sm font-semibold hover:bg-[#0B2C5B] hover:text-white transition" data-testid="article-link-hotels">
                Browse hotels <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/plan-your-trip" className="inline-flex items-center gap-2 rounded-full border-2 border-[#0B2C5B] text-[#0B2C5B] px-5 py-2.5 text-sm font-semibold hover:bg-[#0B2C5B] hover:text-white transition" data-testid="article-link-plan">
                Plan your trip <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/things-to-do" className="inline-flex items-center gap-2 rounded-full border-2 border-[#0B2C5B] text-[#0B2C5B] px-5 py-2.5 text-sm font-semibold hover:bg-[#0B2C5B] hover:text-white transition" data-testid="article-link-things">
                Things to do <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="bg-white py-16 md:py-24 border-t border-slate-100">
          <div className="container-site">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-10">
              <div>
                <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#2AB7E8] mb-2">Keep reading</div>
                <h2 className="font-serif-display text-3xl md:text-4xl font-bold text-[#0B2C5B]">Related articles</h2>
              </div>
              <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B2C5B] hover:text-[#FF8C00]">
                See all articles <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8" data-testid="related-articles">
              {related.slice(0, 4).map((r) => (
                <Link key={r.id} to={`/blog/${r.slug}`} className="group block">
                  <div className="rounded-2xl overflow-hidden aspect-[4/3] mb-4">
                    <img src={r.hero_image} alt={r.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#2AB7E8] mb-2">
                    {formatDate(r.published_at || r.created_at)}
                  </div>
                  <h3 className="font-serif-display text-xl font-bold text-[#0B2C5B] leading-tight group-hover:text-[#FF8C00] transition-colors">
                    {r.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <EmailCapture source={`article-${article.slug}`} />
    </article>
  );
};

export default ArticlePage;
